/**
 * Omni Search Component
 *
 * Universal search bar for the top navigation:
 * - Search categories, tags, work packages, work items
 * - Color-coded results with rounded highlights
 * - 30-character previews
 * - Keyboard navigation (arrow keys, Enter to select)
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { Input, Dropdown, Tag, Typography, Empty } from 'antd';
import { SearchOutlined, FileTextOutlined, TagOutlined, AppstoreOutlined, ProjectOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { templatesApi, instancesApi, categoriesApi, tagsApi } from '@api/index';

const { Text } = Typography;

interface SearchResult {
  id: string;
  type: 'category' | 'tag' | 'template' | 'instance';
  title: string;
  preview?: string;
  color?: string;
  path: string;
}

export default function OmniSearch() {
  const navigate = useNavigate();
  const inputRef = useRef<any>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Fetch all searchable data
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesApi.list(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const { data: tags = [] } = useQuery({
    queryKey: ['tags'],
    queryFn: () => tagsApi.list(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: templates = [] } = useQuery({
    queryKey: ['templates', 'search'],
    queryFn: () => templatesApi.list(),
    staleTime: 2 * 60 * 1000,
  });

  const { data: instances = [] } = useQuery({
    queryKey: ['instances', 'search'],
    queryFn: () => instancesApi.list({ includeCompleted: false }),
    staleTime: 2 * 60 * 1000,
  });

  // Filter and rank results
  const results: SearchResult[] = useCallback(() => {
    if (!query.trim()) return [];

    const q = query.toLowerCase();
    const matches: SearchResult[] = [];

    // 1. Categories (highest priority)
    categories
      .filter((c) => c.name.toLowerCase().includes(q))
      .slice(0, 3)
      .forEach((c) => {
        matches.push({
          id: c.id,
          type: 'category',
          title: c.name,
          color: c.color,
          path: `/templates?category=${c.id}`,
        });
      });

    // 2. Tags
    tags
      .filter((t) => t.name.toLowerCase().includes(q))
      .slice(0, 3)
      .forEach((t) => {
        matches.push({
          id: t.id,
          type: 'tag',
          title: t.name,
          color: t.color,
          path: `/templates?tag=${t.id}`,
        });
      });

    // 3. Work Packages (templates)
    templates
      .filter((t) => t.title.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q))
      .slice(0, 5)
      .forEach((t) => {
        matches.push({
          id: t.id,
          type: 'template',
          title: t.title,
          preview: t.description?.slice(0, 30) || undefined,
          path: `/templates?id=${t.id}`,
        });
      });

    // 4. Work Items (instances)
    instances
      .filter(
        (i) =>
          i.template?.title?.toLowerCase().includes(q) ||
          i.completionNotes?.toLowerCase().includes(q)
      )
      .slice(0, 5)
      .forEach((i) => {
        matches.push({
          id: i.id,
          type: 'instance',
          title: i.template?.title || 'Work Item',
          preview: i.completionNotes?.slice(0, 30) || undefined,
          path: `/tasks?id=${i.id}`,
        });
      });

    return matches.slice(0, 10); // Max 10 results
  }, [query, categories, tags, templates, instances])();

  // Handle selection
  const handleSelect = useCallback(
    (result: SearchResult) => {
      navigate(result.path);
      setQuery('');
      setOpen(false);
    },
    [navigate]
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!open || results.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (results[selectedIndex]) {
          handleSelect(results[selectedIndex]);
        } else if (query.trim()) {
          navigate(`/search?q=${encodeURIComponent(query.trim())}`);
          setQuery('');
          setOpen(false);
        }
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    },
    [open, results, selectedIndex, handleSelect]
  );

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Global keyboard shortcut: Cmd/Ctrl + K to focus search
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  // Type icon mapping
  const typeIcons: Record<SearchResult['type'], React.ReactNode> = {
    category: <AppstoreOutlined />,
    tag: <TagOutlined />,
    template: <FileTextOutlined />,
    instance: <ProjectOutlined />,
  };

  const typeLabels: Record<SearchResult['type'], string> = {
    category: 'Category',
    tag: 'Tag',
    template: 'Work Package',
    instance: 'Work Item',
  };

  const dropdownContent = (
    <div
      style={{
        background: '#fff',
        borderRadius: 8,
        boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
        maxHeight: 400,
        overflowY: 'auto',
        minWidth: 320,
      }}
    >
      {query.trim() === '' ? (
        <div style={{ padding: 16, color: '#888', fontSize: 13 }}>
          Type to search categories, tags, work packages, and work items...
          <div style={{ marginTop: 8, fontSize: 12, color: '#aaa' }}>
            <kbd style={{ background: '#f0f0f0', padding: '2px 6px', borderRadius: 4 }}>⌘K</kbd> to focus
          </div>
        </div>
      ) : results.length === 0 ? (
        <Empty
          description="No results found"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ padding: 24 }}
        />
      ) : (
        <div style={{ padding: 8 }}>
          {results.map((result, index) => (
            <div
              key={`${result.type}-${result.id}`}
              onClick={() => handleSelect(result)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                borderRadius: 6,
                background: index === selectedIndex ? '#f5f5f5' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              {/* Type icon */}
              <span style={{ color: '#888', fontSize: 14 }}>{typeIcons[result.type]}</span>

              {/* Content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {/* Color badge for categories/tags */}
                  {result.color && (
                    <Tag
                      color={result.color}
                      style={{
                        margin: 0,
                        borderRadius: 10,
                        fontSize: 11,
                        padding: '0 8px',
                      }}
                    >
                      {result.title}
                    </Tag>
                  )}
                  {!result.color && (
                    <Text strong style={{ fontSize: 13 }}>
                      {result.title}
                    </Text>
                  )}
                </div>
                {result.preview && (
                  <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                    {result.preview}...
                  </Text>
                )}
              </div>

              {/* Type label */}
              <Text type="secondary" style={{ fontSize: 11, flexShrink: 0 }}>
                {typeLabels[result.type]}
              </Text>
            </div>
          ))}
          {/* View all results link */}
          <div
            onClick={() => {
              navigate(`/search?q=${encodeURIComponent(query.trim())}`);
              setQuery('');
              setOpen(false);
            }}
            style={{
              padding: '10px 12px',
              cursor: 'pointer',
              borderRadius: 6,
              borderTop: '1px solid #f0f0f0',
              marginTop: 4,
              textAlign: 'center',
            }}
          >
            <Text type="secondary" style={{ fontSize: 13 }}>
              <SearchOutlined style={{ marginRight: 6 }} />
              View all results for &ldquo;{query.trim()}&rdquo;
            </Text>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      popupRender={() => dropdownContent}
      trigger={['click']}
      placement="bottomRight"
    >
      <Input
        ref={inputRef}
        prefix={<SearchOutlined style={{ color: '#999' }} />}
        placeholder="Search... (⌘K)"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        className="header-search"
        style={{
          width: 'clamp(200px, 25vw, 400px)',
          borderRadius: 6,
          transition: 'width 0.2s',
        }}
        allowClear
      />
    </Dropdown>
  );
}
